import {
  useRef,
  useState,
  useEffect,
  useMemo,
  useCallback,
  type CSSProperties,
  type RefObject,
} from 'react';

export type FixedPageSecondaryKind = 'none' | 'table-filter' | 'ref';

/** Layout tokens for list pages with viewport-fixed header stacks. */
export const fixedPageStackClasses = {
  pageContainer:
    'w-full flex flex-col min-h-0 h-[calc(100vh-var(--header-height))] max-h-[calc(100vh-var(--header-height))]',
  scrollContainer: 'flex-1 min-h-0 overflow-y-auto',
  secondaryFixedTop:
    'top-[calc(var(--header-height)+var(--page-header-height))]',
} as const;

export interface UseFixedPageStackOptions {
  /** Which secondary fixed band is mounted (if any) */
  secondaryKind: FixedPageSecondaryKind;
  /** Re-bind secondary ref measurement when conditional UI changes (filters, view mode, etc.) */
  secondaryRefDeps?: unknown[];
}

export interface UseFixedPageStackResult {
  pageHeaderRef: RefObject<HTMLDivElement | null>;
  secondaryStackRef: RefObject<HTMLDivElement | null>;
  onTableFilterHeightChange: (height: number) => void;
  scrollPaddingTop: number;
  pageHeaderHeight: number;
  pageContainerClassName: string;
  pageContainerStyle: CSSProperties;
  scrollContainerClassName: string;
  scrollContainerStyle: CSSProperties;
  secondaryFixedTopClass: string;
}

/**
 * Measures fixed page header (+ optional filter/table header) heights for scroll padding.
 * Does not include `--header-height` — CommonLayout already offsets the main area.
 */
export function useFixedPageStack({
  secondaryKind,
  secondaryRefDeps = [],
}: UseFixedPageStackOptions): UseFixedPageStackResult {
  const pageHeaderRef = useRef<HTMLDivElement>(null);
  const secondaryStackRef = useRef<HTMLDivElement>(null);
  const [pageHeaderHeight, setPageHeaderHeight] = useState(80);
  const [tableFilterHeight, setTableFilterHeight] = useState(0);
  const [secondaryRefHeight, setSecondaryRefHeight] = useState(0);

  useEffect(() => {
    const el = pageHeaderRef.current;
    if (!el) return;
    const report = () => setPageHeaderHeight(el.getBoundingClientRect().height);
    report();
    const observer = new ResizeObserver(report);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (secondaryKind !== 'ref') {
      setSecondaryRefHeight(0);
      return;
    }
    const el = secondaryStackRef.current;
    if (!el) {
      setSecondaryRefHeight(0);
      return;
    }
    const report = () => setSecondaryRefHeight(el.getBoundingClientRect().height);
    report();
    const observer = new ResizeObserver(report);
    observer.observe(el);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- caller supplies conditional-render deps
  }, [secondaryKind, ...secondaryRefDeps]);

  useEffect(() => {
    if (secondaryKind !== 'table-filter') {
      setTableFilterHeight(0);
    }
  }, [secondaryKind]);

  const onTableFilterHeightChange = useCallback((height: number) => {
    setTableFilterHeight(height);
  }, []);

  const scrollPaddingTop = useMemo(() => {
    if (secondaryKind === 'none') return pageHeaderHeight;
    if (secondaryKind === 'table-filter') {
      return pageHeaderHeight + tableFilterHeight;
    }
    return pageHeaderHeight + secondaryRefHeight;
  }, [secondaryKind, pageHeaderHeight, tableFilterHeight, secondaryRefHeight]);

  const pageContainerStyle = useMemo(
    () =>
      ({
        '--page-header-height': `${pageHeaderHeight}px`,
      }) as CSSProperties,
    [pageHeaderHeight],
  );

  const scrollContainerStyle = useMemo(
    () => ({ paddingTop: scrollPaddingTop }),
    [scrollPaddingTop],
  );

  return {
    pageHeaderRef,
    secondaryStackRef,
    onTableFilterHeightChange,
    scrollPaddingTop,
    pageHeaderHeight,
    pageContainerClassName: fixedPageStackClasses.pageContainer,
    pageContainerStyle,
    scrollContainerClassName: fixedPageStackClasses.scrollContainer,
    scrollContainerStyle,
    secondaryFixedTopClass: fixedPageStackClasses.secondaryFixedTop,
  };
}
